import { ColorTokenInput } from './ColorTokenInput';
import { FontFamilySelect } from './FontFamilySelect';
import { SizeSlider } from './SizeSlider';
import { PresetSelector } from './PresetSelector';
import { NumberSlider } from './NumberSlider';

interface TokenDefinition {
  label: string;
  description?: string;
  category: string;
  editable: string;
  type: 'color' | 'fontFamily' | 'size' | 'shadow' | 'number';
  default: string | number;
  control?: {
    type: 'slider' | 'dropdown' | 'preset';
    options?: Array<{ value: string; label: string }>;
    min?: number;
    max?: number;
    step?: number;
  };
}

interface TokenInputProps {
  id: string;
  definition: TokenDefinition;
  value: string | number;
  onChange: (value: string) => void;
  readOnly?: boolean;
}

export function TokenInput({
  id,
  definition,
  value,
  onChange,
  readOnly = false,
}: TokenInputProps) {
  const { type, control, label, description } = definition;

  // Color token
  if (type === 'color') {
    return (
      <ColorTokenInput
        id={id}
        label={label}
        description={description}
        value={String(value)}
        onChange={onChange}
        readOnly={readOnly}
      />
    );
  }

  // Font family with dropdown
  if (type === 'fontFamily' && control?.type === 'dropdown' && control.options) {
    return (
      <FontFamilySelect
        id={id}
        label={label}
        description={description}
        value={String(value)}
        options={control.options}
        onChange={onChange}
        readOnly={readOnly}
      />
    );
  }

  // Size with slider
  if (type === 'size' && control?.type === 'slider') {
    return (
      <SizeSlider
        id={id}
        label={label}
        description={description}
        value={value}
        min={control.min ?? 0}
        max={control.max ?? 100}
        step={control.step ?? 1}
        onChange={onChange}
        readOnly={readOnly}
      />
    );
  }

  // Size/Shadow/Other with preset selector
  if (control?.type === 'preset' && control.options) {
    const previewType = type === 'shadow' ? 'shadow' : type === 'size' ? 'spacing' : 'default';

    return (
      <PresetSelector
        id={id}
        label={label}
        description={description}
        value={String(value)}
        options={control.options}
        onChange={onChange}
        readOnly={readOnly}
        previewType={previewType}
      />
    );
  }

  // Number with slider
  if (type === 'number' && control?.type === 'slider') {
    return (
      <NumberSlider
        id={id}
        label={label}
        description={description}
        value={value}
        min={control.min ?? 0}
        max={control.max ?? 10}
        step={control.step ?? 0.1}
        onChange={onChange}
        readOnly={readOnly}
      />
    );
  }

  // Fallback: simple text input
  return (
    <div className="token-input space-y-2">
      <div>
        <label htmlFor={id} className="block text-sm font-medium text-gray-700">
          {label}
        </label>
        {description && (
          <p className="text-xs text-gray-500 mt-0.5">{description}</p>
        )}
      </div>
      <input
        id={id}
        type="text"
        value={String(value)}
        onChange={(e) => onChange(e.target.value)}
        disabled={readOnly}
        className="input w-full"
      />
    </div>
  );
}
